// MaintenanceRequest.js - UPDATED: email removed, single-row submit, tentative date fixed, recurrence validation fixed

import React, { useState, useEffect } from "react";
import "./MaintenanceRequest.css";
import Axios from 'axios';
import { Host } from '../../App';

const MaintenanceForm = () => {
  const today = new Date().toISOString().slice(0, 10);
  var User = JSON.parse(window.localStorage.getItem("Name"));
  var token = (window.localStorage.getItem("acessToken"));
  const [isLoading, setIsLoading] = useState(false);
  const [isManager, setIsManager] = useState(false);
  const [requestType, setRequestType] = useState('emergent'); // 'emergent' or 'prescheduled'

  // Check if user is manager (Stage 2 or Admin)
  useEffect(() => {
    const checkManagerStatus = async () => {
      try {
        const response = await Axios.get(`https://` + Host + `/api/user-info`, {
          params: { token }
        });
        if (response.data) {
          const stage = response.data.Stage;
          const ranked = response.data.Ranked;
          setIsManager(stage === '2' || ranked === 'Admin');
        }
      } catch (error) {
        console.error('Error checking manager status:', error);
      }
    };
    checkManagerStatus();
  }, [token]);

  const initialFormState = {
    Request_date: today,
    name: User || "",
    maintenance_category: "Facility",
    equipment_maintenance: "",
    facility_maintenance: "",
    issue_description: "",
    priority: "Low",
    tentative_completion_date: "",
    outside_assistance: "No",
    agency_name: "",
    comments: "",
    // Pre-scheduled fields
    recurrence_type: "none", // 'none', 'daily', 'weekly', 'biweekly', 'monthly', 'quarterly', 'yearly'
    recurrence_day: "",
    recurrence_date: "",
    schedule_start_date: today,
    schedule_end_date: "",
    is_recurring: false,
    parent_ticket_id: null,
    version: 1
  };

  const [formData, setFormData] = useState(initialFormState);

  const [errors, setErrors] = useState({});
  const [showRecurrenceOptions, setShowRecurrenceOptions] = useState(false);

  // A tentative completion date is only relevant when there's a single,
  // fixed deadline: every emergent request, or a prescheduled request
  // that is NOT recurring. Recurring prescheduled tasks use
  // schedule_start_date / recurrence_* instead.
  const showTentativeCompletionDate =
    requestType === 'emergent' || (requestType === 'prescheduled' && !showRecurrenceOptions);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear error for this field
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleRequestTypeChange = (type) => {
    setRequestType(type);
    // Reset recurrence fields when switching to emergent
    if (type === 'emergent') {
      setFormData({
        ...formData,
        recurrence_type: 'none',
        recurrence_day: '',
        recurrence_date: '',
        schedule_start_date: today,
        schedule_end_date: '',
        is_recurring: false,
        parent_ticket_id: null,
        version: 1
      });
      setShowRecurrenceOptions(false);
    } else {
      // Set default schedule start date to today for prescheduled
      // IMPORTANT: Do NOT set is_recurring to true by default
      setFormData({
        ...formData,
        schedule_start_date: today,
        is_recurring: false // Changed from true to false
      });
      setShowRecurrenceOptions(false); // Reset recurrence options when switching
    }
  };

  const toggleRecurrence = () => {
    setShowRecurrenceOptions(!showRecurrenceOptions);
    if (!showRecurrenceOptions) {
      setFormData({
        ...formData,
        is_recurring: true,
        schedule_start_date: today,
        tentative_completion_date: '' // not used while recurring
      });
    } else {
      setFormData({
        ...formData,
        is_recurring: false,
        recurrence_type: 'none',
        recurrence_day: '',
        recurrence_date: '',
        schedule_end_date: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.issue_description.trim()) {
      newErrors.issue_description = 'Issue description is required';
    }

    if (!formData.maintenance_category) {
      newErrors.maintenance_category = 'Maintenance category is required';
    }

    // Validate pre-scheduled fields
    if (requestType === 'prescheduled') {
      if (!formData.schedule_start_date) {
        newErrors.schedule_start_date = 'Schedule start date is required';
      }

      // ONLY validate recurrence fields if recurrence is actually enabled
      if (showRecurrenceOptions && formData.is_recurring) {
        if (formData.recurrence_type === 'none') {
          newErrors.recurrence_type = 'Please select a recurrence type';
        }

        // For monthly/quarterly/yearly recurrence, a day-of-month is required
        if (
          ['monthly', 'quarterly', 'yearly'].includes(formData.recurrence_type) &&
          !formData.recurrence_date
        ) {
          newErrors.recurrence_type = 'Please select a day of month for this recurrence';
        }

        // For weekly/biweekly recurrence, a day-of-week is required
        if (
          ['weekly', 'biweekly'].includes(formData.recurrence_type) &&
          !formData.recurrence_day
        ) {
          newErrors.recurrence_type = 'Please select a day of week for this recurrence';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      window.alert('Please fix the errors before submitting');
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        token: token,
        formData: formData,
        requestType: requestType,
        isRecurringEnabled: showRecurrenceOptions // Send this to backend to know if recurrence is actually enabled
      };

      // This submits exactly ONE ticket. The backend only ever inserts a
      // single row per submission — recurring instances are not
      // pre-generated; the calendar expands occurrences from the stored
      // recurrence pattern (recurrence_type / recurrence_day /
      // recurrence_date / schedule_start_date / schedule_end_date).
      const response = await Axios.post(`https://` + Host + `/maintenance/api/tickets`, payload);

      if (response.status === 200) {
        const message = requestType === 'prescheduled'
          ? 'Pre-scheduled maintenance request submitted successfully!'
          : 'Maintenance request submitted successfully!';
        window.alert(response.data.message || message);

        // Reset form but keep name and date
        setFormData({
          ...initialFormState,
          name: User || ""
        });
        setRequestType('emergent');
        setShowRecurrenceOptions(false);
      }
    } catch (error) {
      console.error('Error submitting request:', error);
      window.alert(error.response?.data?.error || 'Failed to submit request. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleSubmit();
    }
  };

  // Render recurrence options based on type
  const renderRecurrenceOptions = () => {
    if (!showRecurrenceOptions && requestType === 'prescheduled') {
      return null;
    }

    return (
      <tr>
        <td colSpan="2" style={{ padding: '20px', background: '#f8fafc' }}>
          <div className="recurrence-section">
            <h4 className="recurrence-title">
              <i className="fas fa-sync-alt"></i> Recurrence Settings
            </h4>

            <div className="form-row">
              <div className="form-group">
                <label>Recurrence Type</label>
                <select
                  className="form-control"
                  name="recurrence_type"
                  value={formData.recurrence_type}
                  onChange={handleChange}
                >
                  <option value="none">None (One-time)</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-Weekly</option>
                  <option value="monthly">Monthly (Same Date)</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>

              {(formData.recurrence_type === 'weekly' || formData.recurrence_type === 'biweekly') && (
                <div className="form-group">
                  <label>Day of Week</label>
                  <select
                    className="form-control"
                    name="recurrence_day"
                    value={formData.recurrence_day}
                    onChange={handleChange}
                  >
                    <option value="">Select Day</option>
                    <option value="Monday">Monday</option>
                    <option value="Tuesday">Tuesday</option>
                    <option value="Wednesday">Wednesday</option>
                    <option value="Thursday">Thursday</option>
                    <option value="Friday">Friday</option>
                    <option value="Saturday">Saturday</option>
                    <option value="Sunday">Sunday</option>
                  </select>
                </div>
              )}

              {(formData.recurrence_type === 'monthly' || formData.recurrence_type === 'quarterly' || formData.recurrence_type === 'yearly') && (
                <div className="form-group">
                  <label>Day of Month</label>
                  <select
                    className="form-control"
                    name="recurrence_date"
                    value={formData.recurrence_date}
                    onChange={handleChange}
                  >
                    <option value="">Select Day</option>
                    {[...Array(31)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>{i + 1}</option>
                    ))}
                  </select>
                  {errors.recurrence_type && <div className="invalid-feedback d-block">{errors.recurrence_type}</div>}
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Schedule Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  name="schedule_start_date"
                  value={formData.schedule_start_date}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Schedule End Date (Optional)</label>
                <input
                  type="date"
                  className="form-control"
                  name="schedule_end_date"
                  value={formData.schedule_end_date}
                  onChange={handleChange}
                />
                <small>Leave empty for indefinite</small>
              </div>
            </div>

            <div className="recurrence-info">
              <i className="fas fa-info-circle"></i>
              <span>This ticket will show up on the calendar on every occurrence date based on the pattern above. Only one ticket record is created.</span>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="Maintenance_request" onKeyPress={handleKeyPress}>
      <div className="request-header">
        <h2>New Maintenance Request</h2>
        <p className="request-subtitle">Fill in the details below to submit a maintenance request</p>
        {isManager && (
          <div className="manager-badge">
            <span className="badge badge-info">Manager Access - You can approve requests</span>
          </div>
        )}
      </div>

      {/* Request Type Selection */}
      <div className="request-type-section">
        <div className="request-type-toggle">
          <button
            className={`type-btn ${requestType === 'emergent' ? 'active-emergent' : ''}`}
            onClick={() => handleRequestTypeChange('emergent')}
          >
            <i className="fas fa-exclamation-triangle"></i>
            <span>Emergent Request</span>
            <small>One-time, immediate maintenance</small>
          </button>
          <button
            className={`type-btn ${requestType === 'prescheduled' ? 'active-prescheduled' : ''}`}
            onClick={() => handleRequestTypeChange('prescheduled')}
          >
            <i className="fas fa-calendar-alt"></i>
            <span>Pre-Scheduled Request</span>
            <small>Recurring or planned maintenance</small>
          </button>
        </div>

        {requestType === 'prescheduled' && (
          <div className="recurrence-toggle">
            <label className="switch-label">
              <input
                type="checkbox"
                checked={showRecurrenceOptions}
                onChange={toggleRecurrence}
              />
              <span className="switch-slider"></span>
              Enable Recurrence
            </label>
          </div>
        )}
      </div>

      <table className="table table-bordered" id="Purchase_table">
        <thead className="Maintenance_header">
          <tr>
            <th scope="col" style={{ width: "25%" }}>FIELD</th>
            <th scope="col" style={{ width: "75%" }}>INPUT</th>
          </tr>
        </thead>
        <tbody>
          {/* Request Type Display */}
          <tr>
            <td className="field-label">
              REQUEST TYPE <span className="required">*</span>
            </td>
            <td>
              <span className={`request-type-badge ${requestType}`}>
                {requestType === 'emergent' ? '🚨 Emergent' : '📅 Pre-Scheduled'}
              </span>
              <small className="text-muted" style={{ marginLeft: '10px' }}>
                {requestType === 'emergent'
                  ? 'One-time maintenance request'
                  : 'Planned or recurring maintenance'}
              </small>
            </td>
          </tr>

          {/* Request Date - Fixed */}
          <tr>
            <td className="field-label">
              REQUEST DATE <span className="required">*</span>
            </td>
            <td>
              <input
                type="date"
                className="form-control"
                name="Request_date"
                style={{ width: "100%" }}
                value={formData.Request_date}
                onChange={handleChange}
                disabled
              />
              <small className="text-muted">Auto-set to today's date</small>
            </td>
          </tr>

          {/* Name - Fixed */}
          <tr>
            <td className="field-label">
              REQUESTED BY <span className="required">*</span>
            </td>
            <td>
              <input
                type="text"
                className="form-control"
                name="name"
                style={{ width: "100%" }}
                value={formData.name}
                onChange={handleChange}
                disabled
              />
              <small className="text-muted">Auto-filled from your login</small>
            </td>
          </tr>

          {/* Maintenance Category */}
          <tr>
            <td className="field-label">
              MAINTENANCE CATEGORY <span className="required">*</span>
            </td>
            <td>
              <select
                className={`form-control ${errors.maintenance_category ? 'is-invalid' : ''}`}
                name="maintenance_category"
                style={{ width: "100%" }}
                value={formData.maintenance_category}
                onChange={handleChange}
              >
                <option value="Facility">Facility</option>
                <option value="Equipment">Equipment</option>
                <option value="Electrical">Electrical</option>
                <option value="Plumbing">Plumbing</option>
                <option value="HVAC">HVAC</option>
                <option value="Other">Other</option>
              </select>
              {errors.maintenance_category && <div className="invalid-feedback">{errors.maintenance_category}</div>}
            </td>
          </tr>

          {/* Equipment Maintenance */}
          <tr>
            <td className="field-label">EQUIPMENT MAINTENANCE</td>
            <td>
              <input
                type="text"
                className="form-control"
                name="equipment_maintenance"
                style={{ width: "100%" }}
                value={formData.equipment_maintenance}
                onChange={handleChange}
                placeholder="Specify equipment details (if applicable)"
              />
            </td>
          </tr>

          {/* Facility Maintenance */}
          <tr>
            <td className="field-label">FACILITY MAINTENANCE</td>
            <td>
              <input
                type="text"
                className="form-control"
                name="facility_maintenance"
                style={{ width: "100%" }}
                value={formData.facility_maintenance}
                onChange={handleChange}
                placeholder="Specify facility details (if applicable)"
              />
            </td>
          </tr>

          {/* Issue Description */}
          <tr>
            <td className="field-label">
              ISSUE DESCRIPTION <span className="required">*</span>
            </td>
            <td>
              <textarea
                className={`form-control ${errors.issue_description ? 'is-invalid' : ''}`}
                name="issue_description"
                rows="4"
                style={{ width: "100%" }}
                value={formData.issue_description}
                onChange={handleChange}
                placeholder="Describe the issue in detail..."
              />
              {errors.issue_description && <div className="invalid-feedback">{errors.issue_description}</div>}
              <small className="text-muted">
                {formData.issue_description.length}/500 characters
              </small>
            </td>
          </tr>

          {/* Priority - Dropdown */}
          <tr>
            <td className="field-label">
              PRIORITY <span className="required">*</span>
            </td>
            <td>
              <select
                className="form-control"
                name="priority"
                style={{ width: "100%" }}
                value={formData.priority}
                onChange={handleChange}
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </select>
            </td>
          </tr>

          {/* Tentative Completion Date - hidden whenever recurrence is active */}
          {showTentativeCompletionDate && (
            <tr>
              <td className="field-label">TENTATIVE COMPLETION DATE</td>
              <td>
                <input
                  type="date"
                  className="form-control"
                  name="tentative_completion_date"
                  style={{ width: "100%" }}
                  value={formData.tentative_completion_date}
                  onChange={handleChange}
                />
                <small className="text-muted">Your best estimate for completion</small>
              </td>
            </tr>
          )}

          {/* Outside Assistance - Dropdown */}
          <tr>
            <td className="field-label">OUTSIDE ASSISTANCE</td>
            <td>
              <select
                className="form-control"
                name="outside_assistance"
                style={{ width: "100%" }}
                value={formData.outside_assistance}
                onChange={handleChange}
              >
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </td>
          </tr>

          {/* Agency Name */}
          <tr>
            <td className="field-label">AGENCY NAME</td>
            <td>
              <input
                type="text"
                className="form-control"
                name="agency_name"
                style={{ width: "100%" }}
                value={formData.agency_name}
                onChange={handleChange}
                placeholder="Enter agency name (if outside assistance is Yes)"
                disabled={formData.outside_assistance === 'No'}
              />
            </td>
          </tr>

          {/* Comments */}
          <tr>
            <td className="field-label">COMMENTS</td>
            <td>
              <textarea
                className="form-control"
                name="comments"
                rows="3"
                style={{ width: "100%" }}
                value={formData.comments}
                onChange={handleChange}
                placeholder="Additional comments or notes..."
              />
            </td>
          </tr>

          {/* Recurrence Options - Only for Pre-scheduled */}
          {requestType === 'prescheduled' && renderRecurrenceOptions()}

          {/* Submit Button */}
          <tr>
            <td colSpan="2" className="mr-actions">
              <div className="button-group">
                <button
                  className="btn btn-primary submit-btn"
                  onClick={handleSubmit}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane"></i>
                      {requestType === 'prescheduled' ? ' Schedule Maintenance' : ' Submit Request'}
                    </>
                  )}
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => window.history.back()}
                  disabled={isLoading}
                >
                  Cancel
                </button>
              </div>
              <small className="text-muted">Press Ctrl+Enter to submit</small>
            </td>
          </tr>
        </tbody>
      </table>

      {isLoading && (
        <div className="overlay">
          <div className="loading-container">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p>Submitting your request...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaintenanceForm;